import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { toast } from 'sonner';
import { ArrowLeft, ArrowRight, Check, Camera, Video, Users } from 'lucide-react';
import QuestionnaireRecap from '@/components/QuestionnaireRecap';

const QuestionnaireClient = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [prestations, setPrestations] = useState([]);
  const [questionnaireSettings, setQuestionnaireSettings] = useState({
    show_prices_in_questionnaire: true,
    questionnaire_email: 'kolnoaprod@gmail.com'
  });
  
  const [formData, setFormData] = useState({
    // Client info
    nom: '',
    prenom: '',
    email: '',
    telephone: '',
    adresse: '',
    code_postal: '',
    ville: '',
    // Event info
    event_type: '',
    prestation_type: 'photo_video',
    vedettes: '',
    nb_invites: '',
    has_vin_honneur: false,
    has_henne: false,
    dates: [],
    comments: '',
    // Selections
    selected_prestations: [],
    // Mariage specific
    mariee_prenom: '',
    mariee_telephone: '',
    mariee_adresse: '',
    marie_prenom: '',
    marie_telephone: '',
    marie_adresse: '',
    // Bar Mitsvah specific
    enfant_prenom: '',
    papa_prenom: '',
    papa_telephone: '',
    maman_prenom: '',
    maman_telephone: '',
    // Dates
    date_mairie: '',
    heure_mairie: '',
    adresse_mairie: '',
    date_synagogue: '',
    heure_synagogue: '',
    adresse_synagogue: '',
    date_reception: '',
    heure_reception: '',
    adresse_reception: '',
    date_henne: '',
    heure_henne: '',
    adresse_henne: '',
    barmitzvah_type: '', // synagogue, reception, both, chabbat
    // Autre - demande libre
    autre_type_projet: '', // film_institutionnel, pub, video_reseaux, clip, autre
    autre_description: '',
    autre_budget: '',
    autre_delai: '',
    wants_magnets: false,
  });

  useEffect(() => {
    loadPrestations();
    loadQuestionnaireSettings();
  }, []);

  const loadPrestations = async () => {
    try {
      const res = await axios.get(`${API}/prestations`);
      setPrestations(res.data);
    } catch (error) {
      console.error('Error loading prestations:', error);
    }
  };

  const loadQuestionnaireSettings = async () => {
    try {
      const res = await axios.get(`${API}/questionnaire-settings`);
      setQuestionnaireSettings(res.data);
    } catch (error) {
      console.error('Error loading questionnaire settings:', error);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const togglePrestation = (id) => {
    setFormData(prev => ({
      ...prev,
      selected_prestations: prev.selected_prestations.includes(id)
        ? prev.selected_prestations.filter(p => p !== id)
        : [...prev.selected_prestations, id]
    }));
  };

  // Options spécifiques pour le formulaire
  const [wantsSecondPhotographer, setWantsSecondPhotographer] = useState(false);
  const [wantsMagnets, setWantsMagnets] = useState(false);
  const [wantsDrone, setWantsDrone] = useState(false);
  const [wantsExtraCameraSynagogue, setWantsExtraCameraSynagogue] = useState(false);
  const [wantsExtraCameraReception, setWantsExtraCameraReception] = useState(false);
  const [wantsSecondPhotographerBM, setWantsSecondPhotographerBM] = useState(false);
  const [recommendedInitialized, setRecommendedInitialized] = useState(false);

  // Génère les prestations recommandées automatiquement selon les choix
  const getRecommendedPrestations = () => {
    const recommended = [];
    const { event_type, prestation_type, nb_invites, date_mairie, has_henne } = formData;
    
    const findPrestation = (nameIncludes) => {
      return prestations.find(p => p.name.toLowerCase().includes(nameIncludes.toLowerCase()));
    };

    if (event_type === 'mariage') {
      // 1. MAIRIE / CÉRÉMONIE CIVILE (en premier)
      if (date_mairie) {
        if (prestation_type === 'photo_video' || prestation_type === 'video') {
          const videoMairie = findPrestation('vidéaste mairie');
          if (videoMairie) recommended.push({ ...videoMairie, section: 'mairie', displayName: '1 Vidéaste Mairie' });
        }
        if (prestation_type === 'photo_video' || prestation_type === 'photo') {
          const photoMairie = findPrestation('photographe mairie');
          if (photoMairie) recommended.push({ ...photoMairie, section: 'mairie', displayName: '1 Photographe Mairie' });
        }
      }

      // 2. JOUR J - Vidéo
      if (prestation_type === 'photo_video' || prestation_type === 'video') {
        const videoMariage = findPrestation('forfait vidéo mariage');
        if (videoMariage) recommended.push({ ...videoMariage, section: 'jourj_video', displayName: 'Forfait Vidéo Mariage 2 Caméras Jour J' });
      }

      // 3. JOUR J - Photo
      if (prestation_type === 'photo_video' || prestation_type === 'photo') {
        // Si moins de 250 invités = 1 photographe, sinon 2
        if (nb_invites === '>250') {
          const photo2 = findPrestation('forfait photo mariage');
          if (photo2) recommended.push({ ...photo2, section: 'jourj_photo', displayName: 'Forfait Photo Mariage 2 Photographes Jour J' });
        } else {
          // 1 photographe par défaut + option deuxième
          const photoReception = findPrestation('photographe réception');
          if (photoReception) recommended.push({ ...photoReception, section: 'jourj_photo', displayName: 'Forfait Photo 1 Photographe Jour J' });
        }
      }

      // Henné si sélectionné
      if (has_henne) {
        if (prestation_type === 'photo_video' || prestation_type === 'video') {
          const videoHenne = findPrestation('forfait vidéo henné');
          if (videoHenne) recommended.push({ ...videoHenne, section: 'henne', displayName: 'Forfait Vidéo Henné' });
        }
        if (prestation_type === 'photo_video' || prestation_type === 'photo') {
          const photoHenne = findPrestation('forfait photo henné');
          if (photoHenne) recommended.push({ ...photoHenne, section: 'henne', displayName: 'Forfait Photo Henné' });
        }
      }
    }

    // Bar Mitzvah
    if (event_type === 'barmitzvah') {
      if (prestation_type === 'photo_video' || prestation_type === 'video') {
        // Par défaut : forfait 1 caméra (synagogue + réception)
        const videoBM1 = findPrestation('forfait vidéo bm (1 caméra)');
        if (videoBM1) recommended.push({ ...videoBM1, section: 'bm_video', displayName: 'Vidéo Bar-Mitsvah Synagogue et Réception (1 Caméra)' });
      }
      if (prestation_type === 'photo_video' || prestation_type === 'photo') {
        const photoBM = findPrestation('forfait photo bm (1 photographe)');
        if (photoBM) recommended.push({ ...photoBM, section: 'bm_photo', displayName: 'Photo Bar-Mitsvah Synagogue et Réception (1 Photographe)' });
      }
    }

    // Brit Mila
    if (event_type === 'brit_mila') {
      if (prestation_type === 'photo_video' || prestation_type === 'video') {
        const videoBrit = findPrestation('forfait vidéo brit mila');
        if (videoBrit) recommended.push({ ...videoBrit, section: 'jourj_video', displayName: 'Forfait Vidéo Brit Mila' });
      }
      if (prestation_type === 'photo_video' || prestation_type === 'photo') {
        const photoBrit = findPrestation('forfait photo brit mila');
        if (photoBrit) recommended.push({ ...photoBrit, section: 'jourj_photo', displayName: 'Forfait Photo Brit Mila' });
      }
    }

    // Coupe de cheveux - sélection auto selon option, pas de magnets
    if (event_type === 'coupe_cheveux') {
      if (prestation_type === 'photo_video' || prestation_type === 'video') {
        const videoCoupe = findPrestation('forfait vidéo coupe de cheveux');
        if (videoCoupe) recommended.push({ ...videoCoupe, section: 'coupe_video', displayName: 'Forfait Vidéo Coupe de Cheveux' });
      }
      if (prestation_type === 'photo_video' || prestation_type === 'photo') {
        const photoCoupe = findPrestation('forfait photo coupe de cheveux');
        if (photoCoupe) recommended.push({ ...photoCoupe, section: 'coupe_photo', displayName: 'Forfait Photo Coupe de Cheveux' });
      }
    }

    return recommended;
  };

  // Vérifier si on doit masquer les Magnets (pour autre uniquement)
  const shouldShowMagnets = () => {
    return formData.event_type !== 'autre';
  };

  // Retourne l'option Magnets adaptée au type d'événement et nombre d'invités
  const getMagnetsOption = () => {
    if (!shouldShowMagnets()) return null;
    
    // Pour Coupe de cheveux et Brit Mila : Pack de 100
    if (['coupe_cheveux', 'brit_mila'].includes(formData.event_type)) {
      return prestations.find(p => p.name.includes('Pack Magnets (100'));
    }
    
    // Pour les autres événements : selon nombre d'invités
    const { nb_invites } = formData;
    if (nb_invites === '<150') {
      return prestations.find(p => p.name.includes('Magnets (200'));
    } else if (nb_invites === '150-250') {
      return prestations.find(p => p.name.includes('Magnets (300'));
    } else {
      return prestations.find(p => p.name.includes('Magnets (400'));
    }
  };

  // Retourne l'option Drone
  const getDroneOption = () => {
    return prestations.find(p => p.name.toLowerCase().includes('drone'));
  };

  // Retourne l'option deuxième photographe (mariage)
  const getSecondPhotographerOption = () => {
    return prestations.find(p => p.name.toLowerCase().includes('photographe réception'));
  };

  // Vidéaste Réception supplémentaire (500€)
  const getExtraVideasteReception = () => {
    return prestations.find(p => p.name.toLowerCase().includes('vidéaste réception'));
  };

  // Photographe Réception supplémentaire (800€)
  const getExtraPhotographeReception = () => {
    return prestations.find(p => p.name.toLowerCase().includes('photographe réception'));
  };

  // Vidéaste supplémentaire synagogue (utilise vidéaste synagogue BM)
  const getExtraVideasteSynagogue = () => {
    return prestations.find(p => p.name.toLowerCase().includes('vidéaste synagogue'));
  };

  const buildDatesArray = () => {
    const dates = [];
    if (formData.date_mairie) {
      dates.push({ date: formData.date_mairie, heure: formData.heure_mairie, lieu: 'Mairie', adresse: formData.adresse_mairie, type: 'Mariage Civil' });
    }
    if (formData.date_synagogue) {
      dates.push({ date: formData.date_synagogue, heure: formData.heure_synagogue, lieu: 'Synagogue', adresse: formData.adresse_synagogue, type: 'Synagogue' });
    }
    if (formData.date_reception) {
      dates.push({ date: formData.date_reception, heure: formData.heure_reception, lieu: 'Réception', adresse: formData.adresse_reception, type: 'Réception' });
    }
    if (formData.has_henne && formData.date_henne) {
      dates.push({ date: formData.date_henne, heure: formData.heure_henne, lieu: 'Henné', adresse: formData.adresse_henne, type: 'Henné' });
    }
    return dates;
  };

  const buildVedettes = () => {
    if (formData.event_type === 'mariage') {
      return `${formData.mariee_prenom || formData.prenom} & ${formData.marie_prenom || ''}`;
    } else if (['barmitzvah', 'coupe_cheveux'].includes(formData.event_type)) {
      return formData.enfant_prenom || formData.prenom;
    }
    return formData.prenom;
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const payload = {
        ...formData,
        vedettes: buildVedettes(),
        dates: buildDatesArray(),
      };
      
      const res = await axios.post(`${API}/questionnaire/submit`, payload);
      toast.success(
        'Merci pour votre demande de devis ! Vous allez recevoir une première proposition de devis dans les plus brefs délais par e-mail. Nous restons à votre entière disposition pour toutes questions ! L\'équipe Kolnoa',
        { duration: 6000 }
      );
      
      // Wait 3 seconds then redirect to homepage
      setTimeout(() => {
        navigate('/');
      }, 3000);
    } catch (error) {
      console.error('Error submitting:', error);
      toast.error('Erreur lors de l\'envoi du formulaire');
    } finally {
      setLoading(false);
    }
  };

  const getFilteredPrestations = () => {
    return prestations.filter(p => {
      if (p.event_type === 'all') return true;
      if (formData.event_type === 'barmitzvah' && p.event_type === 'barmitzvah') return true;
      if (formData.event_type === p.event_type) return true;
      return false;
    });
  };

  const isValidEmail = (email) => {
    if (!email) return false;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const canProceed = () => {
    switch (step) {
      case 1:
        return formData.nom && formData.prenom && isValidEmail(formData.email) && formData.telephone;
      case 2:
        // Vérifier que le type d'événement est sélectionné
        if (!formData.event_type) return false;
        // Pour "autre" et "coupe_cheveux", pas besoin de nombre d'invités
        if (formData.event_type === 'autre' || formData.event_type === 'coupe_cheveux') {
          return true;
        }
        // Pour les autres types, le nombre d'invités est obligatoire
        return !!formData.nb_invites;
      case 3:
        return true;
      case 4:
        return formData.selected_prestations.length > 0;
      default:
        return true;
    }
  };

  const totalSteps = 5;

  return (
    <div className="min-h-screen bg-stone-50 py-8 px-4">
      {/* Header */}
      <div className="max-w-3xl mx-auto mb-8">
        <Link to="/" className="inline-flex items-center text-stone-600 hover:text-stone-800 mb-6">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Retour à l'accueil
        </Link>
        <div className="text-center">
          <img 
            src="https://customer-assets.emergentagent.com/job_wedding-erp/artifacts/6y9ywpy7_image.png" 
            alt="Kolnoa" 
            className="h-16 mx-auto mb-4"
          />
          <h1 className="font-display text-3xl font-bold text-stone-800 mb-2">Demande de devis</h1>
          <p className="text-stone-600">Remplissez ce formulaire pour recevoir votre devis personnalisé</p>
        </div>
      </div>

      {/* Progress */}
      <div className="max-w-3xl mx-auto mb-8">
        <div className="flex items-center justify-between">
          {[1, 2, 3, 4, 5].map((s) => (
            <div key={s} className="flex items-center">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center font-medium transition-colors ${
                s < step ? 'bg-amber-600 text-white' :
                s === step ? 'bg-amber-500 text-white' :
                'bg-stone-200 text-stone-500'
              }`}>
                {s < step ? <Check className="h-5 w-5" /> : s}
              </div>
              {s < totalSteps && (
                <div className={`w-12 md:w-24 h-1 mx-2 ${s < step ? 'bg-amber-500' : 'bg-stone-200'}`} />
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Form */}
      <div className="max-w-3xl mx-auto">
        <Card className="shadow-lg">
          <CardContent className="p-8">
            {/* Step 1: Client Info */}
            {step === 1 && (
              <div className="space-y-6 animate-fade-in">
                <CardHeader className="px-0 pt-0">
                  <CardTitle>Vos coordonnées</CardTitle>
                  <CardDescription>Ces informations nous permettront de vous contacter</CardDescription>
                </CardHeader>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="nom">Nom *</Label>
                    <Input
                      id="nom"
                      value={formData.nom}
                      onChange={(e) => handleChange('nom', e.target.value)}
                      placeholder="Votre nom"
                      data-testid="input-nom"
                    />
                  </div>
                  <div>
                    <Label htmlFor="prenom">Prénom *</Label>
                    <Input
                      id="prenom"
                      value={formData.prenom}
                      onChange={(e) => handleChange('prenom', e.target.value)}
                      placeholder="Votre prénom"
                      data-testid="input-prenom"
                    />
                  </div>
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="email">Email *</Label>
                    <Input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => handleChange('email', e.target.value)}
                      placeholder="votre@email.com"
                      required
                      data-testid="input-email"
                    />
                    {formData.email && !isValidEmail(formData.email) && (
                      <p className="text-sm text-rose-600 mt-1">Veuillez entrer une adresse email valide</p>
                    )}
                  </div>
                  <div>
                    <Label htmlFor="telephone">Téléphone *</Label>
                    <Input
                      id="telephone"
                      value={formData.telephone}
                      onChange={(e) => handleChange('telephone', e.target.value)}
                      placeholder="06 XX XX XX XX"
                      data-testid="input-telephone"
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="adresse">Adresse complète</Label>
                  <Input
                    id="adresse"
                    value={formData.adresse}
                    onChange={(e) => handleChange('adresse', e.target.value)}
                    placeholder="Numéro et rue"
                    data-testid="input-adresse"
                  />
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="code_postal">Code postal</Label>
                    <Input
                      id="code_postal"
                      value={formData.code_postal}
                      onChange={(e) => handleChange('code_postal', e.target.value)}
                      placeholder="75000"
                      data-testid="input-code-postal"
                    />
                  </div>
                  <div>
                    <Label htmlFor="ville">Ville</Label>
                    <Input
                      id="ville"
                      value={formData.ville}
                      onChange={(e) => handleChange('ville', e.target.value)}
                      placeholder="Paris"
                      data-testid="input-ville"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Step 2: Event Type */}
            {step === 2 && (
              <div className="space-y-6 animate-fade-in">
                <CardHeader className="px-0 pt-0">
                  <CardTitle>Type d'événement</CardTitle>
                  <CardDescription>Quel événement souhaitez-vous immortaliser ?</CardDescription>
                </CardHeader>
                <RadioGroup
                  value={formData.event_type}
                  onValueChange={(value) => handleChange('event_type', value)}
                  className="grid md:grid-cols-2 gap-4"
                >
                  {[
                    { value: 'mariage', label: 'Mariage', icon: '💍' },
                    { value: 'barmitzvah', label: 'Bar/Bat Mitsvah', icon: '✡️' },
                    { value: 'brit_mila', label: 'Brit Mila', icon: '👶' },
                    { value: 'coupe_cheveux', label: 'Coupe de Cheveux', icon: '✂️' },
                    { value: 'autre', label: 'Autre projet', icon: '🎬' },
                  ].map((option) => (
                    <div key={option.value} className="relative">
                      <RadioGroupItem
                        value={option.value}
                        id={option.value}
                        className="peer sr-only"
                      />
                      <Label
                        htmlFor={option.value}
                        className="flex items-center gap-4 p-4 border-2 rounded-xl cursor-pointer transition-all peer-data-[state=checked]:border-amber-500 peer-data-[state=checked]:bg-amber-50 hover:bg-stone-50"
                        data-testid={`event-type-${option.value}`}
                      >
                        <span className="text-3xl">{option.icon}</span>
                        <span className="font-medium text-stone-800">{option.label}</span>
                      </Label>
                    </div>
                  ))}
                </RadioGroup>

                {/* Choix photo/vidéo - masqué pour "Autre" */}
                {formData.event_type !== 'autre' && (
                <div className="pt-4">
                  <Label className="mb-3 block">Que recherchez-vous ?</Label>
                  <RadioGroup
                    value={formData.prestation_type}
                    onValueChange={(value) => handleChange('prestation_type', value)}
                    className="flex flex-wrap gap-4"
                  >
                    {[
                      { value: 'photo_video', label: 'Photo & Vidéo', icon: Camera },
                      { value: 'video', label: 'Vidéo uniquement', icon: Video },
                      { value: 'photo', label: 'Photo uniquement', icon: Camera },
                    ].map((option) => (
                      <div key={option.value} className="relative">
                        <RadioGroupItem
                          value={option.value}
                          id={`presta-${option.value}`}
                          className="peer sr-only"
                        />
                        <Label
                          htmlFor={`presta-${option.value}`}
                          className="flex items-center gap-2 px-4 py-2 border-2 rounded-lg cursor-pointer transition-all peer-data-[state=checked]:border-amber-500 peer-data-[state=checked]:bg-amber-50 hover:bg-stone-50"
                          data-testid={`presta-type-${option.value}`}
                        >
                          <option.icon className="h-4 w-4" />
                          <span>{option.label}</span>
                        </Label>
                      </div>
                    ))}
                  </RadioGroup>
                </div>
                )}

                {/* Nombre d'invités - masqué pour Autre et Coupe de cheveux */}
                {formData.event_type !== 'autre' && formData.event_type !== 'coupe_cheveux' && (
                  <div className="pt-4">
                    <Label className="mb-3 block">Nombre d'invités estimé *</Label>
                    <Select 
                      value={formData.nb_invites} 
                      onValueChange={(v) => handleChange('nb_invites', v)}
                      required
                    >
                      <SelectTrigger data-testid="select-nb-invites">
                        <SelectValue placeholder="Sélectionnez le nombre d'invités" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="<150">Moins de 150</SelectItem>
                        <SelectItem value="150-250">Entre 150 et 250</SelectItem>
                        <SelectItem value=">250">Plus de 250</SelectItem>
                      </SelectContent>
                    </Select>
                </div>
                )}
              </div>
            )}

            {/* Step 3: Event Details */}
            {step === 3 && (
              <div className="space-y-6 animate-fade-in">
                <CardHeader className="px-0 pt-0">
                  <CardTitle>{formData.event_type === 'autre' ? 'Décrivez votre projet' : 'Détails de l\'événement'}</CardTitle>
                  <CardDescription>{formData.event_type === 'autre' ? 'Parlez-nous de ce que vous recherchez' : 'Informations sur votre événement'}</CardDescription>
                </CardHeader>

                {/* AUTRE - Questionnaire libre */}
                {formData.event_type === 'autre' && (
                  <div className="space-y-6">
                    <div>
                      <Label className="mb-3 block">Type de projet</Label>
                      <RadioGroup
                        value={formData.autre_type_projet}
                        onValueChange={(value) => handleChange('autre_type_projet', value)}
                        className="grid md:grid-cols-2 gap-3"
                      >
                        {[
                          { value: 'film_institutionnel', label: 'Film institutionnel / Corporate' },
                          { value: 'pub', label: 'Publicité / Spot promotionnel' },
                          { value: 'video_reseaux', label: 'Vidéo réseaux sociaux' },
                          { value: 'clip', label: 'Clip musical' },
                          { value: 'reportage', label: 'Reportage / Documentaire' },
                          { value: 'formation', label: 'Vidéo de formation' },
                          { value: 'evenement_pro', label: 'Événement professionnel' },
                          { value: 'autre_projet', label: 'Autre' },
                        ].map((option) => (
                          <div key={option.value} className="relative">
                            <RadioGroupItem value={option.value} id={`projet-${option.value}`} className="peer sr-only" />
                            <Label
                              htmlFor={`projet-${option.value}`}
                              className="flex items-center gap-2 p-3 border-2 rounded-lg cursor-pointer transition-all peer-data-[state=checked]:border-amber-500 peer-data-[state=checked]:bg-amber-50 hover:bg-stone-50"
                            >
                              <span>{option.label}</span>
                            </Label>
                          </div>
                        ))}
                      </RadioGroup>
                    </div>

                    <div>
                      <Label>Décrivez votre projet en détail</Label>
                      <Textarea
                        value={formData.autre_description}
                        onChange={(e) => handleChange('autre_description', e.target.value)}
                        placeholder="Expliquez-nous ce que vous recherchez : le contexte, le message à transmettre, le style souhaité, la durée approximative..."
                        className="min-h-[150px]"
                        data-testid="input-autre-description"
                      />
                    </div>

                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <Label>Budget approximatif (optionnel)</Label>
                        <Select value={formData.autre_budget} onValueChange={(v) => handleChange('autre_budget', v)}>
                          <SelectTrigger>
                            <SelectValue placeholder="Sélectionnez" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="<1000">Moins de 1 000€</SelectItem>
                            <SelectItem value="1000-3000">1 000€ - 3 000€</SelectItem>
                            <SelectItem value="3000-5000">3 000€ - 5 000€</SelectItem>
                            <SelectItem value="5000-10000">5 000€ - 10 000€</SelectItem>
                            <SelectItem value=">10000">Plus de 10 000€</SelectItem>
                            <SelectItem value="a_definir">À définir ensemble</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label>Délai souhaité</Label>
                        <Select value={formData.autre_delai} onValueChange={(v) => handleChange('autre_delai', v)}>
                          <SelectTrigger>
                            <SelectValue placeholder="Sélectionnez" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="urgent">Urgent (moins de 2 semaines)</SelectItem>
                            <SelectItem value="1mois">Dans le mois</SelectItem>
                            <SelectItem value="2-3mois">2-3 mois</SelectItem>
                            <SelectItem value="flexible">Flexible</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    <div>
                      <Label>Commentaires ou informations complémentaires</Label>
                      <Textarea
                        value={formData.comments}
                        onChange={(e) => handleChange('comments', e.target.value)}
                        placeholder="Avez-vous déjà un script ? Des références visuelles ? Des contraintes particulières ?"
                      />
                    </div>
                  </div>
                )}

                {/* Mariage specific */}
                {formData.event_type === 'mariage' && (
                  <>
                    <div className="grid md:grid-cols-2 gap-6">
                      <div className="space-y-4 p-4 bg-pink-50 rounded-xl">
                        <h3 className="font-semibold text-stone-800">La Mariée</h3>
                        <div>
                          <Label>Prénom</Label>
                          <Input
                            value={formData.mariee_prenom}
                            onChange={(e) => handleChange('mariee_prenom', e.target.value)}
                            placeholder="Prénom de la mariée"
                            data-testid="input-mariee-prenom"
                          />
                        </div>
                        <div>
                          <Label>Téléphone</Label>
                          <Input
                            value={formData.mariee_telephone}
                            onChange={(e) => handleChange('mariee_telephone', e.target.value)}
                            placeholder="Téléphone"
                          />
                        </div>
                        <div>
                          <Label>Adresse (avec code, interphone, étage)</Label>
                          <Textarea
                            value={formData.mariee_adresse}
                            onChange={(e) => handleChange('mariee_adresse', e.target.value)}
                            placeholder="Adresse complète pour les préparatifs"
                          />
                        </div>
                      </div>
                      <div className="space-y-4 p-4 bg-blue-50 rounded-xl">
                        <h3 className="font-semibold text-stone-800">Le Marié</h3>
                        <div>
                          <Label>Prénom</Label>
                          <Input
                            value={formData.marie_prenom}
                            onChange={(e) => handleChange('marie_prenom', e.target.value)}
                            placeholder="Prénom du marié"
                            data-testid="input-marie-prenom"
                          />
                        </div>
                        <div>
                          <Label>Téléphone</Label>
                          <Input
                            value={formData.marie_telephone}
                            onChange={(e) => handleChange('marie_telephone', e.target.value)}
                            placeholder="Téléphone"
                          />
                        </div>
                        <div>
                          <Label>Adresse (avec code, interphone, étage)</Label>
                          <Textarea
                            value={formData.marie_adresse}
                            onChange={(e) => handleChange('marie_adresse', e.target.value)}
                            placeholder="Adresse complète pour les préparatifs"
                          />
                        </div>
                      </div>
                    </div>

                    <div className="space-y-4">
                      <h3 className="font-semibold text-stone-800">Cérémonie Civile (Mairie)</h3>
                      <div className="grid md:grid-cols-3 gap-4">
                        <div>
                          <Label>Date</Label>
                          <Input
                            type="date"
                            value={formData.date_mairie}
                            onChange={(e) => handleChange('date_mairie', e.target.value)}
                            data-testid="input-date-mairie"
                          />
                        </div>
                        <div>
                          <Label>Heure</Label>
                          <Input
                            type="time"
                            value={formData.heure_mairie}
                            onChange={(e) => handleChange('heure_mairie', e.target.value)}
                          />
                        </div>
                        <div>
                          <Label>Adresse de la Mairie</Label>
                          <Input
                            value={formData.adresse_mairie}
                            onChange={(e) => handleChange('adresse_mairie', e.target.value)}
                            placeholder="Adresse complète"
                          />
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Checkbox
                          id="vin_honneur"
                          checked={formData.has_vin_honneur}
                          onCheckedChange={(checked) => handleChange('has_vin_honneur', checked)}
                        />
                        <Label htmlFor="vin_honneur">Vin d'honneur après la cérémonie civile</Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Checkbox
                          id="has_henne"
                          checked={formData.has_henne}
                          onCheckedChange={(checked) => handleChange('has_henne', checked)}
                        />
                        <Label htmlFor="has_henne">Henné prévu</Label>
                      </div>
                    </div>

                    {formData.has_henne && (
                      <div className="space-y-4 p-4 bg-orange-50 rounded-xl">
                        <h3 className="font-semibold text-stone-800">Henné</h3>
                        <div className="grid md:grid-cols-3 gap-4">
                          <div>
                            <Label>Date</Label>
                            <Input
                              type="date"
                              value={formData.date_henne}
                              onChange={(e) => handleChange('date_henne', e.target.value)}
                            />
                          </div>
                          <div>
                            <Label>Heure</Label>
                            <Input
                              type="time"
                              value={formData.heure_henne}
                              onChange={(e) => handleChange('heure_henne', e.target.value)}
                            />
                          </div>
                          <div>
                            <Label>Adresse</Label>
                            <Input
                              value={formData.adresse_henne}
                              onChange={(e) => handleChange('adresse_henne', e.target.value)}
                              placeholder="Adresse du Henné"
                            />
                          </div>
                        </div>
                      </div>
                    )}

                    <div className="space-y-4">
                      <h3 className="font-semibold text-stone-800">Cérémonie Religieuse</h3>
                      <div className="grid md:grid-cols-3 gap-4">
                        <div>
                          <Label>Date</Label>
                          <Input
                            type="date"
                            value={formData.date_synagogue}
                            onChange={(e) => handleChange('date_synagogue', e.target.value)}
                          />
                        </div>
                        <div>
                          <Label>Heure</Label>
                          <Input
                            type="time"
                            value={formData.heure_synagogue}
                            onChange={(e) => handleChange('heure_synagogue', e.target.value)}
                          />
                        </div>
                        <div>
                          <Label>Adresse</Label>
                          <Input
                            value={formData.adresse_synagogue}
                            onChange={(e) => handleChange('adresse_synagogue', e.target.value)}
                            placeholder="Adresse de la cérémonie"
                          />
                        </div>
                      </div>
                    </div>

                    <div className="space-y-4">
                      <h3 className="font-semibold text-stone-800">Réception</h3>
                      <div className="grid md:grid-cols-3 gap-4">
                        <div>
                          <Label>Date</Label>
                          <Input
                            type="date"
                            value={formData.date_reception}
                            onChange={(e) => handleChange('date_reception', e.target.value)}
                            data-testid="input-date-reception"
                          />
                        </div>
                        <div>
                          <Label>Heure</Label>
                          <Input
                            type="time"
                            value={formData.heure_reception}
                            onChange={(e) => handleChange('heure_reception', e.target.value)}
                          />
                        </div>
                        <div>
                          <Label>Adresse</Label>
                          <Input
                            value={formData.adresse_reception}
                            onChange={(e) => handleChange('adresse_reception', e.target.value)}
                            placeholder="Adresse de la réception"
                          />
                        </div>
                      </div>
                    </div>
                  </>
                )}

                {/* Bar Mitzvah / Coupe de cheveux */}
                {['barmitzvah', 'coupe_cheveux'].includes(formData.event_type) && (
                  <>
                    <div>
                      <Label>Prénom de l'enfant</Label>
                      <Input
                        value={formData.enfant_prenom}
                        onChange={(e) => handleChange('enfant_prenom', e.target.value)}
                        placeholder="Prénom"
                        data-testid="input-enfant-prenom"
                      />
                    </div>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="space-y-4 p-4 bg-blue-50 rounded-xl">
                        <h3 className="font-semibold">Le Papa</h3>
                        <Input
                          value={formData.papa_prenom}
                          onChange={(e) => handleChange('papa_prenom', e.target.value)}
                          placeholder="Prénom du papa"
                        />
                        <Input
                          value={formData.papa_telephone}
                          onChange={(e) => handleChange('papa_telephone', e.target.value)}
                          placeholder="Téléphone"
                        />
                      </div>
                      <div className="space-y-4 p-4 bg-pink-50 rounded-xl">
                        <h3 className="font-semibold">La Maman</h3>
                        <Input
                          value={formData.maman_prenom}
                          onChange={(e) => handleChange('maman_prenom', e.target.value)}
                          placeholder="Prénom de la maman"
                        />
                        <Input
                          value={formData.maman_telephone}
                          onChange={(e) => handleChange('maman_telephone', e.target.value)}
                          placeholder="Téléphone"
                        />
                      </div>
                    </div>

                    {formData.event_type === 'barmitzvah' && (
                      <div>
                        <Label className="mb-3 block">Type d'événement</Label>
                        <Select value={formData.barmitzvah_type} onValueChange={(v) => handleChange('barmitzvah_type', v)}>
                          <SelectTrigger data-testid="select-bm-type">
                            <SelectValue placeholder="Sélectionnez" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="synagogue">Synagogue uniquement</SelectItem>
                            <SelectItem value="reception">Réception uniquement</SelectItem>
                            <SelectItem value="both">Synagogue et Réception</SelectItem>
                            <SelectItem value="chabbat">Chabbat Plein</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    )}

                    {(formData.barmitzvah_type === 'synagogue' || formData.barmitzvah_type === 'both') && (
                      <div className="space-y-4">
                        <h3 className="font-semibold text-stone-800">Synagogue (Matin)</h3>
                        <div className="grid md:grid-cols-3 gap-4">
                          <Input type="date" value={formData.date_synagogue} onChange={(e) => handleChange('date_synagogue', e.target.value)} />
                          <Input type="time" value={formData.heure_synagogue} onChange={(e) => handleChange('heure_synagogue', e.target.value)} />
                          <Input value={formData.adresse_synagogue} onChange={(e) => handleChange('adresse_synagogue', e.target.value)} placeholder="Adresse" />
                        </div>
                      </div>
                    )}

                    {(formData.barmitzvah_type === 'reception' || formData.barmitzvah_type === 'both' || formData.barmitzvah_type === 'chabbat') && (
                      <div className="space-y-4">
                        <h3 className="font-semibold text-stone-800">Réception</h3>
                        <div className="grid md:grid-cols-3 gap-4">
                          <Input type="date" value={formData.date_reception} onChange={(e) => handleChange('date_reception', e.target.value)} />
                          <Input type="time" value={formData.heure_reception} onChange={(e) => handleChange('heure_reception', e.target.value)} />
                          <Input value={formData.adresse_reception} onChange={(e) => handleChange('adresse_reception', e.target.value)} placeholder="Adresse" />
                        </div>
                      </div>
                    )}

                    {formData.event_type === 'coupe_cheveux' && (
                      <div className="space-y-4">
                        <h3 className="font-semibold text-stone-800">Événement</h3>
                        <div className="grid md:grid-cols-3 gap-4">
                          <Input type="date" value={formData.date_reception} onChange={(e) => handleChange('date_reception', e.target.value)} />
                          <Input type="time" value={formData.heure_reception} onChange={(e) => handleChange('heure_reception', e.target.value)} />
                          <Input value={formData.adresse_reception} onChange={(e) => handleChange('adresse_reception', e.target.value)} placeholder="Adresse" />
                        </div>
                      </div>
                    )}
                  </>
                )}

                {/* Brit Mila */}
                {formData.event_type === 'brit_mila' && (
                  <>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="space-y-4 p-4 bg-blue-50 rounded-xl">
                        <h3 className="font-semibold">Le Papa</h3>
                        <Input value={formData.papa_prenom} onChange={(e) => handleChange('papa_prenom', e.target.value)} placeholder="Prénom" />
                        <Input value={formData.papa_telephone} onChange={(e) => handleChange('papa_telephone', e.target.value)} placeholder="Téléphone" />
                      </div>
                      <div className="space-y-4 p-4 bg-pink-50 rounded-xl">
                        <h3 className="font-semibold">La Maman</h3>
                        <Input value={formData.maman_prenom} onChange={(e) => handleChange('maman_prenom', e.target.value)} placeholder="Prénom" />
                        <Input value={formData.maman_telephone} onChange={(e) => handleChange('maman_telephone', e.target.value)} placeholder="Téléphone" />
                      </div>
                    </div>
                    <div className="space-y-4">
                      <h3 className="font-semibold text-stone-800">Événement</h3>
                      <div className="grid md:grid-cols-3 gap-4">
                        <Input type="date" value={formData.date_reception} onChange={(e) => handleChange('date_reception', e.target.value)} />
                        <Input type="time" value={formData.heure_reception} onChange={(e) => handleChange('heure_reception', e.target.value)} />
                        <Input value={formData.adresse_reception} onChange={(e) => handleChange('adresse_reception', e.target.value)} placeholder="Adresse" />
                      </div>
                    </div>
                  </>
                )}


                {/* Question Magnets - pour tous sauf Autre */}
                {formData.event_type !== 'autre' && (
                  <Card className="border-purple-200 bg-purple-50">
                    <CardContent className="p-4">
                      <div className="flex items-start gap-3">
                        <div className="text-3xl">🧲</div>
                        <div className="flex-1">
                          <Label className="text-base font-semibold mb-2 block">Souhaitez-vous des magnets personnalisés ?</Label>
                          <p className="text-sm text-stone-600 mb-3">
                            Photos imprimées sous forme de magnets pour vos invités
                          </p>
                          <div className="flex gap-4">
                            <label className="flex items-center gap-2 cursor-pointer">
                              <input
                                type="radio"
                                name="wants_magnets"
                                checked={formData.wants_magnets === true}
                                onChange={() => setFormData(prev => ({ ...prev, wants_magnets: true }))}
                                className="w-4 h-4 text-amber-600"
                              />
                              <span className="text-sm font-medium">Oui</span>
                            </label>
                            <label className="flex items-center gap-2 cursor-pointer">
                              <input
                                type="radio"
                                name="wants_magnets"
                                checked={formData.wants_magnets === false}
                                onChange={() => setFormData(prev => ({ ...prev, wants_magnets: false }))}
                                className="w-4 h-4 text-stone-600"
                              />
                              <span className="text-sm font-medium">Non</span>
                            </label>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )}
              </div>
            )}

            {/* Step 4: Prestations ou Récap pour Autre */}
            {step === 4 && (
              <div className="space-y-6 animate-fade-in">
                {/* Pour "Autre" - Récapitulatif de la demande */}
                {formData.event_type === 'autre' ? (
                  <>
                    <CardHeader className="px-0 pt-0">
                      <CardTitle>Récapitulatif de votre demande</CardTitle>
                      <CardDescription>Vérifiez les informations avant envoi</CardDescription>
                    </CardHeader>

                    <div className="space-y-4">
                      <div className="p-4 bg-amber-50 rounded-xl border border-amber-200">
                        <h3 className="font-semibold text-amber-800 mb-2">Type de projet</h3>
                        <p className="text-stone-700 capitalize">
                          {formData.autre_type_projet?.replace(/_/g, ' ') || 'Non spécifié'}
                        </p>
                      </div>

                      <div className="p-4 bg-stone-50 rounded-xl">
                        <h3 className="font-semibold text-stone-800 mb-2">Description du projet</h3>
                        <p className="text-stone-600 whitespace-pre-wrap">
                          {formData.autre_description || 'Aucune description'}
                        </p>
                      </div>

                      <div className="grid md:grid-cols-2 gap-4">
                        <div className="p-4 bg-stone-50 rounded-xl">
                          <h3 className="font-semibold text-stone-800 mb-2">Budget</h3>
                          <p className="text-stone-600">
                            {formData.autre_budget?.replace(/_/g, ' ') || 'À définir'}
                          </p>
                        </div>
                        <div className="p-4 bg-stone-50 rounded-xl">
                          <h3 className="font-semibold text-stone-800 mb-2">Délai</h3>
                          <p className="text-stone-600">
                            {formData.autre_delai?.replace(/_/g, ' ') || 'À définir'}
                          </p>
                        </div>
                      </div>

                      {formData.comments && (
                        <div className="p-4 bg-stone-50 rounded-xl">
                          <h3 className="font-semibold text-stone-800 mb-2">Commentaires</h3>
                          <p className="text-stone-600">{formData.comments}</p>
                        </div>
                      )}

                      <div className="p-4 bg-blue-50 rounded-xl border border-blue-200">
                        <p className="text-blue-800 text-sm">
                          <strong>Note :</strong> Nous vous recontacterons dans les plus brefs délais pour discuter de votre projet et vous proposer un devis personnalisé.
                        </p>
                      </div>
                    </div>
                  </>
                ) : (
                  /* Pour les événements classiques - Formule adaptée */
                  <>
                <CardHeader className="px-0 pt-0">
                  <CardTitle>Sélectionnez vos prestations</CardTitle>
                  <CardDescription>Choisissez les options qui vous intéressent</CardDescription>
                </CardHeader>

                <QuestionnaireRecap
                  eventType={formData.event_type}
                  eventDetails={{
                    date_mairie: formData.mariee_date_mairie || formData.date_mairie,
                    hour_mairie: formData.heure_mairie,
                    date_henne: formData.date_henne,
                    hour_henne: formData.heure_henne,
                    date_synagogue: formData.date_synagogue,
                    hour_synagogue: formData.heure_synagogue,
                    date_reception: formData.mariee_date_mariage || formData.date_reception,
                    hour_reception: formData.heure_reception
                  }}
                  nbInvites={formData.nb_invites}
                  wantsMagnets={formData.wants_magnets}
                  hasHenne={formData.has_henne}
                  selectedPrestations={formData.selected_prestations || []}
                  onSelectionChange={(newSelection) => setFormData(prev => ({ ...prev, selected_prestations: newSelection }))}
                  showPrices={questionnaireSettings.show_prices_in_questionnaire}
                />
                  </>

                )}
              </div>
            )}

            {/* Step 5: Summary */}
            {step === 5 && (
              <div className="space-y-6 animate-fade-in">
                <CardHeader className="px-0 pt-0">
                  <CardTitle>Récapitulatif</CardTitle>
                  <CardDescription>Vérifiez vos informations avant validation</CardDescription>
                </CardHeader>

                <div className="space-y-4">
                  <div className="p-4 bg-stone-50 rounded-xl">
                    <h3 className="font-semibold mb-2">Vos coordonnées</h3>
                    <p>{formData.prenom} {formData.nom}</p>
                    <p className="text-sm text-stone-600">{formData.email} | {formData.telephone}</p>
                  </div>

                  <div className="p-4 bg-stone-50 rounded-xl">
                    <h3 className="font-semibold mb-2">Événement</h3>
                    <p className="capitalize">{formData.event_type?.replace('_', ' ')}</p>
                    <p className="text-sm text-stone-600">{buildVedettes()}</p>
                  </div>

                  <div className="p-4 bg-amber-50 rounded-xl">
                    <h3 className="font-semibold mb-2">Prestations sélectionnées</h3>
                    {formData.selected_prestations.map((id) => {
                      const presta = prestations.find(p => p.id === id);
                      return presta ? (
                        <div key={id} className="flex justify-between py-1">
                          <span>{presta.name}</span>
                          {questionnaireSettings.show_prices_in_questionnaire && (
                            <span className="font-medium">{presta.price_ht}€ HT</span>
                          )}
                        </div>
                      ) : null;
                    })}
                    {questionnaireSettings.show_prices_in_questionnaire && (
                      <div className="border-t border-amber-200 mt-2 pt-2 flex justify-between font-semibold">
                        <span>Total HT</span>
                        <span>
                          {formData.selected_prestations.reduce((sum, id) => {
                            const presta = prestations.find(p => p.id === id);
                            return sum + (presta?.price_ht || 0);
                          }, 0)}€
                        </span>
                      </div>
                    )}
                  </div>

                  <div>
                    <Label>Commentaires ou demandes spécifiques</Label>
                    <Textarea
                      value={formData.comments}
                      onChange={(e) => handleChange('comments', e.target.value)}
                      placeholder="Vos remarques..."
                      data-testid="input-comments"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Navigation */}
            <div className="flex justify-between pt-8">
              {step > 1 ? (
                <Button
                  variant="outline"
                  onClick={() => setStep(step - 1)}
                  data-testid="btn-previous"
                >
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  Précédent
                </Button>
              ) : (
                <div />
              )}
              
              {step < totalSteps ? (
                <Button
                  onClick={() => setStep(step + 1)}
                  disabled={!canProceed()}
                  className="bg-amber-600 hover:bg-amber-700 text-white"
                  data-testid="btn-next"
                >
                  Suivant
                  <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
              ) : (
                <Button
                  onClick={handleSubmit}
                  disabled={loading || !canProceed()}
                  className="bg-amber-600 hover:bg-amber-700 text-white"
                  data-testid="btn-submit"
                >
                  {loading ? 'Envoi en cours...' : 'Envoyer ma demande'}
                  <Check className="h-4 w-4 ml-2" />
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default QuestionnaireClient;
